import type { CodeSent, PasscodeChannel } from '../types/domain.types';

import type { CodeHold, CodeHoldKind } from './codeEffect';
import type { TimedRefusalCode } from './refusal';

const MS_PER_SECOND = 1000;

export interface ChallengeSession<TTarget, TSent extends CodeSent = CodeSent> {
  readonly key: string;
  readonly target: TTarget;
  readonly sent: TSent;
  readonly resendAt: number;
  readonly expiresAt: number;
  readonly code: string;
  readonly attempt: number;
}

export interface HeldUntil {
  readonly key: string;
  readonly kind: CodeHoldKind;
  readonly reason: TimedRefusalCode;
  readonly seconds: number;
  readonly until: number;
}

export type ChallengeEnd = 'verified' | 'cancelled';

export type ChallengePhase<TTarget, TSent extends CodeSent, TVerified> =
  | { readonly step: 'idle'; readonly endedBy: 'cancelled' | null; readonly last: ChallengeSession<TTarget, TSent> | null }
  | { readonly step: 'code'; readonly session: ChallengeSession<TTarget, TSent> }
  | { readonly step: 'verified'; readonly session: ChallengeSession<TTarget, TSent>; readonly result: TVerified };

export interface ChallengeState<TTarget, TSent extends CodeSent = CodeSent, TVerified = unknown> {
  readonly phase: ChallengePhase<TTarget, TSent, TVerified>;
  readonly hold: HeldUntil | null;
  readonly epoch: number;
}

export type WaitReason = Exclude<TimedRefusalCode, 'LOCKED'>;

export type ChallengeEvent<TTarget, TSent extends CodeSent = CodeSent, TVerified = unknown> =
  | { readonly type: 'sent'; readonly epoch: number; readonly key: string; readonly target: TTarget; readonly sent: TSent; readonly at: number }
  | { readonly type: 'typed'; readonly code: string }
  | { readonly type: 'refused' }
  | {
      readonly type: 'held';
      readonly epoch: number;
      readonly key: string;
      readonly reason: WaitReason;
      readonly seconds: number;
      readonly channel?: PasscodeChannel;
      readonly at: number;
    }
  | { readonly type: 'locked'; readonly key: string; readonly seconds: number; readonly at: number }
  | { readonly type: 'verified'; readonly result: TVerified }
  | { readonly type: 'cancelled' };

export const initialChallenge = <TTarget, TSent extends CodeSent = CodeSent, TVerified = unknown>(): ChallengeState<TTarget, TSent, TVerified> => ({
  phase: { step: 'idle', endedBy: null, last: null },
  hold: null,
  epoch: 0,
});

export const openSessionOf = <TTarget, TSent extends CodeSent, TVerified>(
  phase: ChallengePhase<TTarget, TSent, TVerified>,
): ChallengeSession<TTarget, TSent> | null => (phase.step === 'code' ? phase.session : null);

export const lastSessionOf = <TTarget, TSent extends CodeSent, TVerified>(
  phase: ChallengePhase<TTarget, TSent, TVerified>,
): ChallengeSession<TTarget, TSent> | null => (phase.step === 'idle' ? phase.last : phase.session);

export const endOf = <TTarget, TSent extends CodeSent, TVerified>(phase: ChallengePhase<TTarget, TSent, TVerified>): ChallengeEnd | null => {
  if (phase.step === 'verified') return 'verified';
  return phase.step === 'idle' ? phase.endedBy : null;
};

export const keptDigits = <TTarget>(previous: ChallengeSession<TTarget>, sent: CodeSent, at: number): string =>
  previous.sent.sentOn === sent.sentOn && previous.expiresAt > at ? previous.code : '';

export const liveHoldOf = (hold: HeldUntil | null, key: string | null, now: number): HeldUntil | null =>
  hold !== null && key !== null && hold.key === key && hold.until > now ? hold : null;

export const resumableSession = <TTarget, TSent extends CodeSent, TVerified>(
  state: ChallengeState<TTarget, TSent, TVerified>,
  key: string,
  channel: PasscodeChannel | undefined,
  at: number,
): ChallengeSession<TTarget, TSent> | null => {
  if (state.phase.step === 'verified') return null;
  const previous = lastSessionOf(state.phase);
  if (previous === null || previous.key !== key || previous.expiresAt <= at) return null;
  return channel === undefined || previous.sent.sentOn === channel ? previous : null;
};

const untilOf = (at: number, seconds: number): number => at + seconds * MS_PER_SECOND;

const withSession = <TTarget, TSent extends CodeSent, TVerified>(
  state: ChallengeState<TTarget, TSent, TVerified>,
  update: (session: ChallengeSession<TTarget, TSent>) => ChallengeSession<TTarget, TSent>,
): ChallengeState<TTarget, TSent, TVerified> =>
  state.phase.step === 'code' ? { ...state, phase: { step: 'code', session: update(state.phase.session) } } : state;

const landSent = <TTarget, TSent extends CodeSent, TVerified>(
  state: ChallengeState<TTarget, TSent, TVerified>,
  event: Extract<ChallengeEvent<TTarget, TSent, TVerified>, { type: 'sent' }>,
): ChallengeState<TTarget, TSent, TVerified> => {
  const open = openSessionOf(state.phase);
  const same = open !== null && open.key === event.key ? open : null;
  const session: ChallengeSession<TTarget, TSent> = {
    key: event.key,
    target: event.target,
    sent: event.sent,
    resendAt: untilOf(event.at, event.sent.resendInSeconds),
    expiresAt: untilOf(event.at, event.sent.expiresInSeconds),
    code: same === null ? '' : keptDigits(same, event.sent, event.at),
    attempt: same === null ? 0 : same.attempt,
  };
  return { ...state, phase: { step: 'code', session }, hold: state.hold?.key === event.key ? null : state.hold };
};

const landHeld = <TTarget, TSent extends CodeSent, TVerified>(
  state: ChallengeState<TTarget, TSent, TVerified>,
  event: Extract<ChallengeEvent<TTarget, TSent, TVerified>, { type: 'held' }>,
): ChallengeState<TTarget, TSent, TVerified> => {
  const until = untilOf(event.at, event.seconds);
  const hold: HeldUntil = { key: event.key, kind: 'wait', reason: event.reason, seconds: event.seconds, until };
  if (event.reason === 'TOO_MANY_SENDS') return { ...state, hold };
  if (event.epoch !== state.epoch) return state;
  const resumable = resumableSession(state, event.key, event.channel, event.at);
  if (resumable === null) return { ...state, hold };
  return { ...state, phase: { step: 'code', session: { ...resumable, resendAt: Math.max(resumable.resendAt, until) } } };
};

export const codeChallengeReducer = <TTarget, TSent extends CodeSent, TVerified>(
  state: ChallengeState<TTarget, TSent, TVerified>,
  event: ChallengeEvent<TTarget, TSent, TVerified>,
): ChallengeState<TTarget, TSent, TVerified> => {
  switch (event.type) {
    case 'sent':
      return event.epoch === state.epoch && state.phase.step !== 'verified' ? landSent(state, event) : state;
    case 'typed':
      return withSession(state, (session) => ({ ...session, code: event.code }));
    case 'refused':
      return withSession(state, (session) => ({ ...session, code: '', attempt: session.attempt + 1 }));
    case 'held':
      return state.phase.step === 'verified' ? state : landHeld(state, event);
    case 'locked':
      return {
        ...state,
        hold: { key: event.key, kind: 'locked', reason: 'LOCKED', seconds: event.seconds, until: untilOf(event.at, event.seconds) },
      };
    case 'verified':
      return state.phase.step === 'code' ? { ...state, phase: { step: 'verified', session: state.phase.session, result: event.result } } : state;
    case 'cancelled':
      return { ...state, phase: { step: 'idle', endedBy: 'cancelled', last: lastSessionOf(state.phase) }, epoch: state.epoch + 1 };
  }
};

export const holdEventOf = <TTarget, TSent extends CodeSent, TVerified>(
  hold: CodeHold,
  facts: { readonly epoch: number; readonly key: string; readonly channel?: PasscodeChannel; readonly at: number },
): ChallengeEvent<TTarget, TSent, TVerified> =>
  hold.reason === 'LOCKED'
    ? { type: 'locked', key: facts.key, seconds: hold.seconds, at: facts.at }
    : { type: 'held', epoch: facts.epoch, key: facts.key, reason: hold.reason, seconds: hold.seconds, channel: facts.channel, at: facts.at };
